import { forwardRef, memo, useMemo, useCallback, type ReactNode, type MouseEventHandler, type MouseEvent } from 'react';
import startsWith from 'lodash/startsWith';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import type { TypographyVariant } from '@mui/material';
import Link, { type LinkProps } from '@mui/material/Link';
import Typography, { type TypographyProps } from '@mui/material/Typography';
// local imports
import { Values } from '../helpers/intl';
import { GetComponentProps } from '../helpers/types';
import LinkWithRoute from '../mixins/LinkWithRoute';
// SCSS imports
import { link, boldLink, darkLink, activeLink, disabledLink, disabledText } from './StandardLink.module.scss';

type StandardLinkProps = {
  readonly children?: ReactNode;
  readonly text?: string; // link text translated with React Intl
  readonly values?: Values; // values for link text translation with React Intl
  readonly bold?: boolean;
  readonly dark?: boolean;
  readonly active?: boolean;
  readonly inherit?: boolean;
  readonly variant?: TypographyVariant | 'inherit';
  readonly to?: string; // React Router link
  readonly toState?: object; // React Router link state
  readonly href?: string | null; // static href
  readonly withoutTarget?: boolean; // do not add `target="_blank"` for static href
  readonly link?: string | null; // dynamic href translated with React Intl
  readonly onClick?: MouseEventHandler<HTMLAnchorElement> & MouseEventHandler<HTMLSpanElement>; // click handler function
  readonly className?: string;
  readonly sx?: TypographyProps['sx'] & LinkProps['sx'] & GetComponentProps<typeof LinkWithRoute>['sx'];
};

const StandardLink = forwardRef<HTMLAnchorElement, StandardLinkProps>(({
  children,
  text,
  values,
  bold = false,
  dark = false,
  active = false,
  inherit = false,
  variant,
  to,
  toState,
  href: pureHref,
  withoutTarget = false,
  link: hrefLink,
  onClick,
  className,
  ...props
  // eslint-disable-next-line complexity
}, ref) => {
  const { formatMessage } = useIntl();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement & HTMLSpanElement>): void => {
      if (to || pureHref || hrefLink) event.stopPropagation();
      else event.preventDefault();
      if (onClick) onClick(event);
    },
    [hrefLink, pureHref, to, onClick]
  );

  const [translatedText, translatedHref] = useMemo(() => [
    text && formatMessage({ id: text, defaultMessage: text }, values),
    hrefLink && formatMessage({ id: hrefLink, defaultMessage: hrefLink })
  ], [formatMessage, hrefLink, text, values]);

  if (!children && !translatedText) return null;

  const content = children || translatedText;
  const href = pureHref || translatedHref;

  const disabled = !to && !href && !onClick;

  const clsName = clsx(className, {
    [boldLink]: bold,
    [link]: !bold,
    [activeLink]: active,
    [darkLink]: dark,
    [disabledLink]: !dark && !active && disabled,
    [disabledText]: disabled
  });

  return (disabled && (
    <Typography
        ref={ref}
        className={clsName}
        variant={variant || 'inherit'}
        component="span"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
    >
      {content}
    </Typography>
  )) || (to && (
    <LinkWithRoute
        ref={ref}
        color={active || dark || inherit ? 'inherit' : 'secondary'}
        to={to}
        state={toState}
        variant={variant}
        onClick={handleClick}
        className={clsName}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
    >
      {content}
    </LinkWithRoute>
  )) || (
    <Link
        ref={ref}
        color={active || dark || inherit ? 'inherit' : 'secondary'}
        underline="hover"
        href={href || '#'}
        onClick={handleClick}
        target={href && !withoutTarget && !startsWith(href, 'mailto:') ? '_blank' : undefined}
        rel={href ? 'noopener noreferrer' : undefined}
        variant={variant}
        className={clsName}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
    >
      {content}
    </Link>
  );
});

StandardLink.displayName = 'StandardLink';

export default memo(StandardLink);
